/**
 * Admin appointment queue (M14-T06/T12). amtc_admin sees all and actions the
 * queue (confirm/decline/complete); a hospital_admin sees only their hospital's,
 * read-only (both enforced server-side). No money anywhere.
 */
import { apiFetch, type CappedList } from './client'

export type AdminAppointmentSession = {
  day_of_week: number
  part_of_day: string
  start_time?: string | null
  end_time?: string | null
}

export type AdminAppointment = {
  reference: string
  patient_name: string | null
  patient_phone: string | null
  specialist_name: string
  specialist_slug: string
  specialty: string
  hospital_name: string
  hospital_slug: string
  session: AdminAppointmentSession
  requested_date: string
  consult_fee_pesewas: number | null
  reason_for_visit: string
  status: string
  confirmed_for?: string | null
  decline_reason?: string | null
  created_at: string
}

export const APPOINTMENTS_KEY = ['admin', 'appointment-requests'] as const

/** GET /api/v1/admin/appointment-requests — newest first. `hospitalId` (amtc's
 * "Managing" picker) and `status` are optional; a hospital_admin is scoped
 * server-side regardless. */
export async function fetchAppointments(
  token: string | null,
  hospitalId?: string | null,
  status?: string | null,
  q?: string,
): Promise<CappedList<AdminAppointment>> {
  const qs = new URLSearchParams()
  if (hospitalId) qs.set('hospital_id', hospitalId)
  if (status) qs.set('status', status)
  if (q) qs.set('q', q)
  const tail = qs.toString() ? `?${qs.toString()}` : ''
  return await apiFetch<CappedList<AdminAppointment>>(
    `/api/v1/admin/appointment-requests${tail}`,
    { token },
  )
}

/** POST /{ref}/confirm — amtc_admin only. `confirmedFor` is an ISO datetime. */
export async function confirmAppointment(
  token: string | null,
  reference: string,
  confirmedFor: string,
): Promise<void> {
  await apiFetch(`/api/v1/admin/appointment-requests/${reference}/confirm`, {
    method: 'POST',
    token,
    body: { confirmed_for: confirmedFor },
  })
}

/** POST /{ref}/decline — amtc_admin only. */
export async function declineAppointment(
  token: string | null,
  reference: string,
  reason: string,
): Promise<void> {
  await apiFetch(`/api/v1/admin/appointment-requests/${reference}/decline`, {
    method: 'POST',
    token,
    body: { decline_reason: reason },
  })
}

/** POST /{ref}/complete — amtc_admin only. */
export async function completeAppointment(
  token: string | null,
  reference: string,
): Promise<void> {
  await apiFetch(`/api/v1/admin/appointment-requests/${reference}/complete`, {
    method: 'POST',
    token,
  })
}
